import { TableDashboardItemHelper } from 'src/app/components/table-dashboard-item/table-dashboard-item-helper';
import { TableColumnItem } from 'src/app/components/table-dashboard-item/table-dashboard-item.component';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';

export class CampaignTableDashoardItemHelper {
    public static readonly METRIC_DAYS = [
        0, 1, 3, 7, 14, 30, 60, 90, 180, 360, 720,
    ];
    // 12 weekly days
    // code
    public static readonly WEEKLY_DAYS = [...Array(12 + 1).keys()].map((x) => {
        return { week_no: x, day_no: (x + 1) * 7 };
    });
    public static readonly MONTHLY_DAYS = [...Array(12 + 1).keys()].map((x) => {
        return { month_no: x, day_no: (x + 1) * 30 };
    });

    // getter for all days
    public static get ALL_DAYS(): number[] {
        // combile metricDays, weeklyDays and monthlyDays into single array
        const allDaysSet = new Set([
            ...this.METRIC_DAYS,
            ...this.WEEKLY_DAYS.map((w) => w.day_no),
            ...this.MONTHLY_DAYS.map((m) => m.day_no),
        ]);
        // convert to array
        return Array.from(allDaysSet).sort((a, b) => a - b);
    }

    static metricsGetter(context: IQueryContext) {
        const metrics = CampaignTableDashoardItemHelper.getAllDefaultMetrics(
            context
        )
            // filter out metrics that are not selected in context. If context is undefined, return all metrics
            .filter((metric: TableColumnItem) => {
                return (
                    context?.settings[
                        QueryContextSettingID.CAMPAIGNS_SELECTED_METRICS
                    ]?.includes(metric.id) ?? true
                );
            });
        return metrics;
    }

    static getAllDefaultMetrics(context?: IQueryContext): TableColumnItem[] {
        return [
            // 'costs',
            {
                id: 'costs',
                name: 'Costs',
                nzLeft: false,
                nzAlign: 'right',
                // nzWidth: '200px',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'costs',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // 'income',
            {
                id: 'income',
                nzLeft: false,
                name: 'Income',
                nzAlign: 'right',
                // nzWidth: '200px',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'income',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // 'reattribution_income',
            {
                id: 'reattribution_income',
                nzLeft: false,
                name: 'Reattribution income',
                nzAlign: 'right',
                // nzWidth: '80px',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'reattribution_income',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // 'roas',
            {
                id: 'total_roas',
                nzLeft: false,
                name: 'Total ROAS',
                nzAlign: 'right',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'total_roas',
                    'number'
                ),
                labelFormatter:
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // 'total_profit',
            {
                id: 'total_profit',
                nzLeft: false,
                name: 'Total Profit',
                nzAlign: 'right',
                // nzWidth: '80px',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'total_profit',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // ads_revenue_share
            {
                id: 'ad_revenue_share',
                nzLeft: false,
                name: 'Ad revenue share',
                nzAlign: 'right',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'ads_revenue_share',
                    'number'
                ),
                labelFormatter:
                    EChartsTooltipHelper.tooltipPercentValueFormatter,
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // impressions
            {
                id: 'impressions',
                nzLeft: false,
                name: 'Impressions',
                nzAlign: 'right',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'impressions',
                    'number'
                ),
                labelFormatter:
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // installs
            {
                id: 'installs',
                nzLeft: false,
                name: 'Installs',
                nzAlign: 'right',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'installs',
                    'number'
                ),
                labelFormatter:
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // transactions
            {
                id: 'transactions',
                nzLeft: false,
                name: 'Transactions',
                nzAlign: 'right',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'transactions',
                    'number'
                ),
                labelFormatter:
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // payers
            {
                id: 'payers',
                nzLeft: false,
                name: 'Payers',
                nzAlign: 'right',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'payers',
                    'number'
                ),
                labelFormatter:
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // cpi
            {
                id: 'cpi',
                nzLeft: false,
                name: 'CPI',
                nzAlign: 'right',
                // nzWidth: '0px',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'cpi',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // cpt
            {
                id: 'cpt',
                nzLeft: false,
                name: 'CPT',
                nzAlign: 'right',
                // nzWidth: '50px',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'cpt',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // cpm
            {
                id: 'cpm',
                nzLeft: false,
                name: 'CPM',
                nzAlign: 'right',
                // nzWidth: '50px',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'cpm',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // cppu
            {
                id: 'cppu',
                nzLeft: false,
                name: 'CPPU',
                nzAlign: 'right',
                // nzWidth: '50px',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'cppu',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // cppu_d7
            {
                id: 'cppu_d7',
                nzLeft: false,
                name: 'CPPU D7',
                nzAlign: 'right',
                // nzWidth: '50px',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'cppu_d7',
                    'number'
                ),
                labelFormatter: (value: any) => {
                    return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                        value,
                        context!.settings.currency || 'pln'
                    );
                },
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // payers_d7
            {
                id: 'payers_d7',
                nzLeft: false,
                name: 'Payers D7',
                nzAlign: 'right',
                // nzWidth: '50px',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'payers_d7',
                    'number'
                ),
                labelFormatter:
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // ipm
            {
                id: 'ipm',
                nzLeft: false,
                name: 'IPM',
                nzAlign: 'right',
                // nzWidth: '50px',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'ipm',
                    'number'
                ),
                labelFormatter:
                    EChartsTooltipHelper.tooltipNumberValueFormatter,
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
            // revenue
            ...this.METRIC_DAYS.map((d) => {
                return {
                    id: `revenue_d${d}`,
                    nzLeft: false,
                    name: `Revenue D${d}`,
                    nzAlign: 'right',
                    // nzWidth: '50px',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        `revenue_d${d}`,
                        'number'
                    ),
                    labelFormatter: (value: any) => {
                        return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            value,
                            context!.settings.currency || 'pln'
                        );
                    },
                    colorPalette: {
                        startColor: '#e4f4ef',
                        endColor: '#aabcce',
                        steps: 6,
                    },
                } as TableColumnItem;
            }),
            // running_revenue
            ...this.METRIC_DAYS.map((d) => {
                return {
                    id: `running_revenue_d${d}`,
                    nzLeft: false,
                    name: `Running Revenue D${d}`,
                    nzAlign: 'right',
                    // nzWidth: '50px',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        `running_revenue_d${d}`,
                        'number'
                    ),
                    labelFormatter: (value: any) => {
                        return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            value,
                            context!.settings.currency || 'pln'
                        );
                    },
                    colorPalette: {
                        startColor: '#e4f4ef',
                        endColor: '#aabcce',
                        steps: 6,
                    },
                } as TableColumnItem;
            }),
            // roas
            ...this.METRIC_DAYS.map((d) => {
                return {
                    id: `roas_d${d}`,
                    nzLeft: false,
                    name: `ROAS D${d}`,
                    nzAlign: 'right',
                    // nzWidth: '50px',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        `roas_d${d}`,
                        'number'
                    ),
                    labelFormatter:
                        EChartsTooltipHelper.tooltipPercentValueFormatter,
                    colorPalette: {
                        startColor: '#e4f4ef',
                        endColor: '#aabcce',
                        steps: 6,
                    },
                } as TableColumnItem;
            }),
            ...this.WEEKLY_DAYS.map((w) => {
                return {
                    id: `roas_w${w.week_no}_d${w.day_no}`,
                    nzLeft: false,
                    name: `ROAS W${w.week_no} (D${w.day_no})`,
                    nzAlign: 'right',
                    // nzWidth: '50px',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        `roas_w${w.week_no}_d${w.day_no}`,
                        'number'
                    ),
                    labelFormatter:
                        EChartsTooltipHelper.tooltipPercentValueFormatter,
                    colorPalette: {
                        startColor: '#e4f4ef',
                        endColor: '#aabcce',
                        steps: 6,
                    },
                } as TableColumnItem;
            }),
            ...this.MONTHLY_DAYS.map((m) => {
                return {
                    id: `roas_m${m.month_no}_d${m.day_no}`,
                    nzLeft: false,
                    name: `ROAS M${m.month_no} (D${m.day_no})`,
                    nzAlign: 'right',
                    // nzWidth: '50px',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        `roas_m${m.month_no}_d${m.day_no}`,
                        'number'
                    ),
                    labelFormatter:
                        EChartsTooltipHelper.tooltipPercentValueFormatter,
                    colorPalette: {
                        startColor: '#e4f4ef',
                        endColor: '#aabcce',
                        steps: 6,
                    },
                } as TableColumnItem;
            }),
            // arpu
            ...this.METRIC_DAYS.map((d) => {
                return {
                    id: `arpu_d${d}`,
                    nzLeft: false,
                    name: `ARPU D${d}`,
                    nzAlign: 'right',
                    // nzWidth: '50px',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        `arpu_d${d}`,
                        'number'
                    ),
                    labelFormatter: (value: any) => {
                        return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            value,
                            context!.settings.currency || 'pln'
                        );
                    },
                    colorPalette: {
                        startColor: '#e4f4ef',
                        endColor: '#aabcce',
                        steps: 6,
                    },
                } as TableColumnItem;
            }),
            // cpt
            ...this.METRIC_DAYS.map((d) => {
                return {
                    id: `cpt_d${d}`,
                    nzLeft: false,
                    name: `CPT D${d}`,
                    nzAlign: 'right',
                    // nzWidth: '50px',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        `cpt_d${d}`,
                        'number'
                    ),
                    labelFormatter: (value: any) => {
                        return EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            value,
                            context!.settings.currency || 'pln'
                        );
                    },
                    colorPalette: {
                        startColor: '#e4f4ef',
                        endColor: '#aabcce',
                        steps: 6,
                    },
                } as TableColumnItem;
            }),
            // retention
            ...this.METRIC_DAYS.map((d) => {
                return {
                    id: `retention_d${d}`,
                    nzLeft: false,
                    name: `Retention D${d}`,
                    nzAlign: 'right',
                    // nzWidth: '50px',
                    nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                        `retention_d${d}`,
                        'number'
                    ),
                    // format as %
                    labelFormatter:
                        EChartsTooltipHelper.tooltipPercentValueFormatter,
                    colorPalette: {
                        startColor: '#e4f4ef',
                        endColor: '#aabcce',
                        steps: 6,
                    },
                } as TableColumnItem;
            }),
            // reattributions
            {
                id: 'reattributions',
                nzLeft: false,
                name: 'Reattributions',
                nzAlign: 'right',
                // nzWidth: '50px',
                nzSortFn: TableDashboardItemHelper.defaultSortFnGetter(
                    'reattributions',
                    'number'
                ),
                labelFormatter:
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
                colorPalette: TableDashboardItemHelper.getDefaultColorPalette(),
            },
        ];
    }
}
